
import { useMutation, useQueryClient } from "react-query";
import { apiRequests } from "src/shared/api/api";
import { wsLogger } from "./webSocket";

export const getSenderType = () => {
  return localStorage.getItem("sender_type") || "OPERATOR";
};

export const useSendImage = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    async ({ chatId, base64Data, token, websocket }) => {
      const currentSenderType = getSenderType();
      const messageId = Date.now();
      
      
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        const wsPayload = {
          type: "message",
          token: token,
          sender_type: currentSenderType,
          content: base64Data, 
          message_type: "IMAGE",
          chat_id: chatId,
          message_id: messageId
        };
        
        wsLogger.outgoing('Отправка изображения по WS', { 
          chatId,
          hasImage: !!base64Data,
          dataLength: base64Data?.length 
        });
        websocket.send(JSON.stringify(wsPayload));
        
        return { 
          type: "ws", 
          messageId,
          payload: wsPayload 
        };
      }
      
      
      const apiPayload = {
        chat_id: chatId,
        sender_type: currentSenderType,
        content: base64Data, 
        message_type: "IMAGE",
        status: "SENT",
        source: "WEB"
      };
      
      wsLogger.outgoing('Отправка изображения по API', { chatId });
      const response = await apiRequests.chats.createMessage(apiPayload);
      
      return { 
        type: "api", 
        response 
      };
    },
    {
      onMutate: async (variables) => {
        
        const optimisticMessage = {
          id: `temp_${Date.now()}`,
          chat_id: variables.chatId,
          sender_type: getSenderType(),
          content: "", 
          message_type: "IMAGE",
          status: "SENDING",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          source: "WEB"
        };
        
        
        if (variables.base64Data && variables.base64Data.startsWith('data:image')) {
          optimisticMessage.image_url = variables.base64Data;
        } else {
          optimisticMessage.image_url = `data:image/jpeg;base64,${variables.base64Data}`;
        }
        
        queryClient.setQueryData(
          ['chatMessages', variables.chatId, variables.token],
          (oldData = []) => [...oldData, optimisticMessage]
        );
        
        return { optimisticMessage };
      },
      onSuccess: (result, variables, context) => {
        if (result.type === "api" && result.response) {
          const serverMsg = result.response.data;
          const serverId = serverMsg?.id ?? serverMsg?.message_id;
          queryClient.setQueryData(
            ['chatMessages', variables.chatId, variables.token],
            (oldData = []) => {
              const filtered = oldData.filter(msg =>
                !String(msg.id || msg.message_id || '').startsWith('temp_')
              );
              const withoutThis = serverId == null
                ? filtered
                : filtered.filter(msg => (msg.id || msg.message_id) !== serverId);
              return [...withoutThis, serverMsg];
            }
          );
          queryClient.invalidateQueries(['chats', variables.token]);
        } else if (result.type === "ws") {
          
          wsLogger.info('Изображение отправлено через WS');
        }
      },
      onError: (error, variables, context) => {
        wsLogger.error(error, `Отправка изображения в чат ${variables.chatId}`);
        queryClient.setQueryData(
          ['chatMessages', variables.chatId, variables.token],
          (oldData = []) => {
            if (!oldData) return oldData;
            return oldData.filter(msg =>
              !String(msg.id || msg.message_id || "").startsWith("temp_")
            );
          }
        );
      }
    }
  );
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    async (messageId) => {
      return apiRequests.chats.deleteMessage(messageId);
    },
    {
      onSuccess: (response, messageId) => {
        const queryCache = queryClient.getQueryCache();
        const chatMessagesQueries = queryCache.findAll({ queryKey: ['chatMessages'] });
        
        chatMessagesQueries.forEach(query => {
          queryClient.setQueryData(query.queryKey, (oldData) => {
            if (!oldData) return oldData;
            return oldData.filter(msg => msg.id !== messageId);
          });
        });
      }
    }
  );
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    async ({ chatId, content, token, websocket, client_message_id }) => {
      const currentSenderType = getSenderType();
      const messageId = Date.now();
      
      
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        const wsPayload = {
          type: "message",
          token: token,
          sender_type: currentSenderType,
          content: content,
          message_type: "TEXT",
          chat_id: chatId,
          message_id: messageId,
          client_message_id 
        };
        
        wsLogger.outgoing('Отправка сообщения по WS', wsPayload);
        websocket.send(JSON.stringify(wsPayload));
        
        return { 
          type: "ws", 
          messageId,
          payload: wsPayload 
        };
      }
      
      
      const apiPayload = {
        chat_id: chatId,
        sender_type: currentSenderType,
        content: content,
        message_type: "TEXT",
        status: "SENT",
        source: "WEB"
      };
      
      wsLogger.outgoing('Отправка сообщения по API', apiPayload);
      const response = await apiRequests.chats.createMessage(apiPayload);
      
      return { 
        type: "api", 
        response 
      };
    },
    {
      
      onSuccess: (result, variables, context) => {
        if (result.type === "api" && result.response) {
          const serverMsg = result.response.data;
          const serverId = serverMsg?.id ?? serverMsg?.message_id;
          queryClient.setQueryData(
            ['chatMessages', variables.chatId, variables.token],
            (oldData = []) => {
              const filtered = oldData.filter(msg =>
                !String(msg.id || msg.message_id || '').startsWith('temp_')
              );
              const withoutThis = serverId == null
                ? filtered
                : filtered.filter(msg => (msg.id || msg.message_id) !== serverId);
              return [...withoutThis, serverMsg];
            }
          );
          queryClient.setQueryData(
            ['chats', variables.token],
            (oldData) => {
              if (!oldData) return oldData;
              return oldData.map(chat => {
                if (chat.id === variables.chatId) {
                  return {
                    ...chat,
                    last_message_time: new Date().toISOString(),
                    last_message_preview: variables.content,
                    unread_count: 0 
                  };
                }
                return chat;
              });
            }
          );
        } else if (result.type === "ws") {
          
          wsLogger.info('Сообщение отправлено через WS');
        }
      },
      onError: (error, variables, context) => {
        wsLogger.error(error, `Отправка сообщения в чат ${variables.chatId}`);
        
        
        queryClient.setQueryData(
          ['chatMessages', variables.chatId, variables.token],
          (oldData = []) => {
            if (!oldData) return oldData;
            return oldData.filter(msg => !msg.id.startsWith('temp_'));
          }
        );
      }
    }
  );
};